/**
 * Builds the text sent to the agent when the user uploads a file from the
 * console — and, more importantly, decides WHICH tool to name.
 *
 * The agents have exactly two document-reading paths: `doc_read` for text, and
 * `doc_convert` (which turns `.pdf`/`.docx`/`.zip` into markdown) followed by
 * `doc_read`. **No agent tool can look at an image**, or at audio, video, or an
 * arbitrary binary. Telling an agent to "read" a photo asks for something it
 * cannot do, and the likely result is a confident description of a file nobody
 * opened — so an unrecognised type is reported as unreadable rather than
 * optimistically assumed to be text.
 */

export type UploadKind = 'text' | 'convertible' | 'opaque';

/** One completed upload, as the transcript and the note both see it. */
export interface UploadedFile {
  /** Path relative to the user's documents root, e.g. `tmp/uploads/a1/notes.md`. */
  readonly path: string;
  /** Sanitized basename, for display. */
  readonly name: string;
  /** MIME type the browser reported; '' when it did not recognise the file. */
  readonly contentType: string;
}

/** MIME types `doc_convert` turns into markdown. */
const CONVERTIBLE_TYPES = new Set([
  'application/pdf',
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const CONVERTIBLE_EXTENSIONS = new Set(['pdf', 'docx', 'zip']);

/** Non-`text/*` MIME types that are nonetheless plain text to `doc_read`. */
const TEXT_TYPES = new Set([
  'application/json',
  'application/xml',
  'application/yaml',
  'application/x-yaml',
  'application/javascript',
]);

const TEXT_EXTENSIONS = new Set([
  'txt',
  'md',
  'markdown',
  'csv',
  'tsv',
  'json',
  'yml',
  'yaml',
  'xml',
  'html',
  'css',
  'js',
  'jsx',
  'ts',
  'tsx',
  'py',
  'rb',
  'go',
  'rs',
  'java',
  'sh',
  'sql',
  'log',
]);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
}

/**
 * Classify an upload by what the agents can actually do with it. MIME type
 * first (the browser's own reading of the file), extension as the fallback,
 * and `opaque` when neither is recognised — never a hopeful guess at text.
 */
export function uploadKind(file: { name: string; contentType: string }): UploadKind {
  const type = file.contentType.toLowerCase();
  if (CONVERTIBLE_TYPES.has(type)) {
    return 'convertible';
  }
  if (type.startsWith('text/') || TEXT_TYPES.has(type)) {
    return 'text';
  }
  // A recognised non-text MIME family (image/, audio/, video/, font/) is
  // definitively unreadable — do not let an extension override it. An image
  // renamed to .txt is still an image.
  if (type.length > 0 && !type.startsWith('application/')) {
    return 'opaque';
  }
  const ext = extensionOf(file.name);
  if (CONVERTIBLE_EXTENSIONS.has(ext)) {
    return 'convertible';
  }
  if (TEXT_EXTENSIONS.has(ext)) {
    return 'text';
  }
  return 'opaque';
}

/** The per-file instruction, naming only tools that exist for that type. */
function lineFor(file: UploadedFile): string {
  switch (uploadKind(file)) {
    case 'text':
      return `- \`${file.path}\` — read it with doc_read.`;
    case 'convertible':
      return `- \`${file.path}\` — convert it with doc_convert first, then read the markdown it writes with doc_read.`;
    case 'opaque':
      return `- \`${file.path}\` — you have no tool that can open this file type; do not describe its contents.`;
  }
}

/**
 * The message sent to the agent after one or more uploads complete. Returns ''
 * for an empty list so a caller can never fire an empty turn.
 */
export function buildUploadNote(files: readonly UploadedFile[]): string {
  if (files.length === 0) {
    return '';
  }
  const n = files.length;
  const noun = n === 1 ? 'file' : 'files';
  const pronoun = n === 1 ? 'it' : 'them';
  const demonstrative = n === 1 ? 'This file is' : 'These files are';
  const copy = n === 1 ? 'a permanent copy' : 'permanent copies';
  return [
    `The user uploaded ${n} ${noun} from the web console to your document store:`,
    '',
    ...files.map(lineFor),
    '',
    `${demonstrative} ephemeral and will be deleted in 7 days. Tell the user what you can do with ${pronoun}, and offer to save ${copy} of anything worth keeping.`,
  ].join('\n');
}
